import { useState, useRef } from "react";
import { base44 } from "../../api/base44Client";
import {
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Paper,
  IconButton,
} from "@mui/material";
import {
  Upload,
  Camera,
  CheckCircle2,
  AlertCircle,
  Edit2,
  Check,
  RotateCcw,
} from "lucide-react";

export default function ResidenzaDocumentUpload({ data, onChange }) {
  const [phase, setPhase] = useState("idle"); // idle | uploading | analyzing | review | done | error
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [editAddress, setEditAddress] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef(null);

  async function handleFile(file) {
    if (!file) return;
    setPhase("uploading");
    setErrorMsg("");
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      setUploadedUrl(file_url);
      setPhase("analyzing");

      const res = await base44.functions.invoke("extractDocumentData", {
        file_front_url: file_url,
      });
      const result = res.data;

      if (result.success && result.data) {
        const d = result.data;
        const parts = [
          d.residenza_indirizzo || d.indirizzo,
          d.residenza_civico || d.civico,
          d.residenza_cap || d.cap,
          d.residenza_localita || d.localita || d.citta,
          d.residenza_provincia || d.provincia,
        ].filter(Boolean);
        const addr = parts.join(", ");
        setExtracted({ ...d, _addressString: addr || "" });
        setEditAddress(addr || "");
        setPhase("review");
      } else {
        setExtracted({ _addressString: "" });
        setEditAddress("");
        setPhase("review");
      }
    } catch (e) {
      setErrorMsg("Errore durante il caricamento o l'analisi.");
      setPhase("error");
    }
  }

  function handleConfirm() {
    if (extracted) {
      if (extracted.residenza_indirizzo)
        onChange("residenza_indirizzo", extracted.residenza_indirizzo);
      if (extracted.residenza_civico)
        onChange("residenza_civico", extracted.residenza_civico);
      if (extracted.residenza_cap)
        onChange("residenza_cap", extracted.residenza_cap);
      if (extracted.residenza_localita)
        onChange("residenza_localita", extracted.residenza_localita);
      if (extracted.residenza_provincia)
        onChange("residenza_provincia", extracted.residenza_provincia);
      if (!extracted.residenza_indirizzo && editAddress) {
        onChange("residenza_indirizzo", editAddress);
      }
      if (extracted.nome) onChange("nome", extracted.nome);
      if (extracted.cognome) onChange("cognome", extracted.cognome);
    }
    onChange("documento_residenza_url", uploadedUrl);
    setPhase("done");
  }

  function handleReset() {
    setPhase("idle");
    setUploadedUrl(null);
    setExtracted(null);
    setEditAddress("");
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        Carica documento che attesta la residenza
      </Typography>

      {phase === "idle" && (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<Upload size={18} />}
            onClick={() => {
              fileInputRef.current.removeAttribute("capture");
              fileInputRef.current.click();
            }}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Carica da file
          </Button>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<Camera size={18} />}
            onClick={() => {
              fileInputRef.current.setAttribute("capture", "environment");
              fileInputRef.current.click();
            }}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Scatta foto
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            style={{ display: "none" }}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </Box>
      )}

      {(phase === "uploading" || phase === "analyzing") && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderRadius: 2,
            bgcolor: "action.hover",
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2">
            {phase === "uploading"
              ? "Caricamento in corso…"
              : "Analisi documento in corso…"}
          </Typography>
        </Paper>
      )}

      {phase === "review" && (
        <Alert
          icon={<Edit2 size={20} />}
          severity="info"
          sx={{ borderRadius: 3, "& .MuiAlert-message": { width: "100%" } }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>
            {extracted?._addressString
              ? "Indirizzo rilevato — verifica e conferma"
              : "Indirizzo non rilevato — inserisci manualmente"}
          </AlertTitle>

          {extracted?.nome && (
            <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
              Nome:{" "}
              <strong>
                {extracted.nome} {extracted.cognome}
              </strong>
            </Typography>
          )}

          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Indirizzo di residenza
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={editAddress}
              onChange={(e) => setEditAddress(e.target.value)}
              placeholder="Es. Via Roma 1, 20100 Milano MI"
              sx={{ bgcolor: "white", borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Puoi modificare il testo prima di confermare
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={handleConfirm}
              startIcon={<Check size={16} />}
              sx={{ textTransform: "none" }}
            >
              Conferma e aggiorna
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleReset}
              sx={{ textTransform: "none" }}
            >
              Riprova
            </Button>
          </Box>
        </Alert>
      )}

      {phase === "done" && (
        <Alert
          severity="success"
          sx={{ borderRadius: 3 }}
          action={
            <IconButton size="small" onClick={handleReset} color="inherit">
              <RotateCcw size={16} />
            </IconButton>
          }
        >
          <AlertTitle sx={{ fontWeight: 600 }}>
            Indirizzo aggiornato automaticamente dal documento
          </AlertTitle>
          <Typography variant="body2">{editAddress}</Typography>
        </Alert>
      )}

      {phase === "error" && (
        <Alert
          severity="error"
          sx={{ borderRadius: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleReset}
              sx={{ textTransform: "none" }}
            >
              Riprova
            </Button>
          }
        >
          {errorMsg}
        </Alert>
      )}
    </Box>
  );
}
